import { Phone, ShieldCheck, FileCheck2, ArrowRight, Building, CalendarDays } from "lucide-react";
import { PHONE_DISPLAY, PHONE_LINK } from "@/lib/constants";

const trustBullets = [
  "Intervention rapide 24/48h",
  "Manutention complète",
  "Étages & accès difficiles",
  "Certificat destruction",
  "Transport sécurisé",
];

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-12 md:pt-20 pb-16 md:pb-28 overflow-hidden grain"
      data-testid="hero-section"
    >
      {/* Background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none -z-10"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 0%, rgba(37,99,235,0.10), transparent 40%), radial-gradient(circle at 90% 10%, rgba(2,6,23,0.05), transparent 35%)",
        }}
      />
      <div className="absolute inset-0 -z-10 opacity-[0.05]"
           style={{
             backgroundImage:
               "linear-gradient(to right, #020617 1px, transparent 1px), linear-gradient(to bottom, #020617 1px, transparent 1px)",
             backgroundSize: "48px 48px",
             maskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
             WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
           }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* LEFT */}
        <div className="lg:col-span-7 animate-fade-up">
          <div className="flex flex-nowrap items-center gap-1.5 sm:gap-2">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-blue-200 bg-blue-50 px-2.5 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-xs font-bold uppercase tracking-[0.12em] sm:tracking-[0.18em] text-blue-700 whitespace-nowrap" data-testid="hero-overline">
              <ShieldCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
              Bouches-du-Rhône · 24/48h
            </div>
            <div className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-xs font-bold uppercase tracking-[0.12em] sm:tracking-[0.16em] text-emerald-700 whitespace-nowrap" data-testid="hero-urgency">
              <span className="relative flex h-1.5 w-1.5 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
              </span>
              <CalendarDays className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
              Dispo cette semaine
            </div>
          </div>

          <h1 className="font-display mt-6 md:mt-7 text-[2.25rem] leading-[1.02] sm:text-5xl md:text-6xl lg:text-[4.5rem] font-extrabold tracking-tighter text-slate-950" data-testid="hero-headline">
            Enlèvement et <span className="text-blue-600">destruction confidentielle</span> d'archives dans les Bouches-du-Rhône.
          </h1>

          <p className="mt-6 text-base md:text-xl text-slate-500 leading-relaxed max-w-2xl font-medium" data-testid="hero-subtitle">
            Nous récupérons vos archives directement sur site avec manutention complète,
            <strong className="text-slate-900"> même en étages ou accès difficiles.</strong>
          </p>

          {/* HUGE phone CTA */}
          <div className="mt-9 space-y-3">
            <a
              href={`tel:${PHONE_LINK}`}
              className="pulse-ring group flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 w-full sm:w-auto sm:inline-flex bg-slate-950 text-white font-extrabold py-5 sm:py-6 px-6 sm:px-10 rounded-2xl hover:bg-blue-600 transition-all shadow-2xl shadow-slate-900/30"
              data-testid="hero-call-button"
            >
              <span className="flex items-center gap-2 text-[10px] sm:hidden uppercase tracking-[0.25em] text-white/70 font-bold">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                Réponse immédiate · Appeler
              </span>
              <span className="hidden sm:inline-flex">
                <Phone className="h-8 w-8 group-hover:rotate-12 transition-transform" />
              </span>
              <span className="font-display tracking-tight text-3xl sm:text-4xl">{PHONE_DISPLAY}</span>
            </a>
            <a
              href="#devis"
              className="flex sm:inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-blue-600 text-white font-bold py-4 px-6 rounded-2xl hover:bg-blue-700 transition-all sm:ml-3 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              data-testid="hero-quote-button"
            >
              Devis gratuit en 60s
              <ArrowRight className="h-5 w-5" />
            </a>
          </div>

          {/* Bullets — lighter weight */}
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="hero-bullets">
            {trustBullets.map((b) => (
              <li
                key={b}
                className="flex items-center gap-2.5 text-sm text-slate-700"
                data-testid={`hero-bullet-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <span className="h-5 w-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 20 20" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 10 l3 3 7-7"/>
                  </svg>
                </span>
                <span className="font-medium">{b}</span>
              </li>
            ))}
          </ul>

          {/* Rating */}
          <div className="mt-10 pt-7 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-2">
            <div className="flex items-center gap-2">
              <div className="flex text-amber-500">
                {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
              </div>
              <span className="text-sm font-bold text-slate-900">4.9/5 · 120+ pros en PACA</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Building className="h-4 w-4 text-blue-600" />
              Entreprise locale 13
            </div>
          </div>
        </div>

        {/* RIGHT — visual */}
        <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img
              src="https://customer-assets.emergentagent.com/job_archives-secure-paca/artifacts/x85otuay_je-viens-a-lhopital_mes-droits_dossier-medical.jpg"
              alt="Salle d'archives professionnelles — manutention de dossiers papier sur rayonnages"
              className="w-full h-[340px] sm:h-[440px] md:h-[600px] object-cover"
              loading="eager"
              data-testid="hero-image"
            />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-slate-950/70 to-transparent" />

            <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-bold text-slate-900 shadow">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Interventions cette semaine
            </div>

            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="text-[11px] uppercase tracking-widest text-white/80">Service clé en main</div>
              <div className="font-display text-2xl md:text-3xl font-bold leading-tight">
                Vous ne touchez<br />à <span className="text-blue-300">rien.</span>
              </div>
            </div>
          </div>

          {/* Single floating card */}
          <div className="hidden md:block absolute -left-6 -bottom-6 bg-white rounded-2xl shadow-xl p-4 max-w-[260px]">
            <div className="flex items-center gap-2">
              <FileCheck2 className="h-5 w-5 text-emerald-600" strokeWidth={1.75} />
              <span className="text-sm font-bold">Certificat de destruction</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Remis après chaque intervention. Conforme RGPD.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
